// A render error used to blank the entire page. No message, no stack, no
// hint of which component threw — the app simply vanished, and the only
// way to find out why was to reproduce it with a devtools console open.
//
// This catches it, shows what broke and where, and reports it to the
// server so a failure a user hits once is in the logs afterwards rather
// than living only in their browser.

import React from 'react'
import { API_BASE_URL } from '@/lib/apiClient'

interface Props { children: React.ReactNode; where?: string }
interface State { error: Error | null; stack: string | null }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ stack: info.componentStack ?? null })
    console.error('[render error]', error, info.componentStack)
    // fire-and-forget: the report must never itself break the page
    try {
      void fetch(`${API_BASE_URL}/api/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message ?? error).slice(0, 500),
          stack: String(error?.stack ?? '').slice(0, 4000),
          component_stack: String(info.componentStack ?? '').slice(0, 4000),
          url: window.location.href.slice(0, 500),
          where: this.props.where ?? null,
        }),
      }).catch(() => {})
    } catch { /* reporting is best-effort */ }
  }

  render() {
    const { error, stack } = this.state
    if (!error) return this.props.children
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <h2 className="text-sm font-semibold text-red-800 dark:text-red-200">
            This page failed to render{this.props.where ? ` (${this.props.where})` : ''}
          </h2>
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">
            The rest of the app still works — the error is below, and has been sent to the server.
          </p>
          <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[11px] text-red-900 dark:bg-slate-900 dark:text-red-200">
            {error.message || String(error)}
          </pre>
          {stack && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] text-red-700 dark:text-red-300">Component stack</summary>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10px] text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                {stack}
              </pre>
            </details>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null, stack: null })}
              className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-900/40"
            >Try again</button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            >Reload</button>
          </div>
        </div>
      </div>
    )
  }
}
